// hooks
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

// utils
import { addDays, isBefore, startOfDay } from 'date-fns';
import { parseBackendDateTime, toBackendDateTime } from 'utils/date';
import { getAbsenceRollout } from 'RestAPIs/roster/register-absence';
import {
  createDefaultSetting,
  isPnilContract,
  isZeroHoursContract
} from './workAgreementSetting';
import { MAX_HOURS_PER_DAY, hoursPerDay } from './workingDaysHours';

// types
import type { Dispatch, SetStateAction } from 'react';
import type {
  AbsenceRolloutDutyLine,
  RegisterAbsenceWorkAgreement
} from 'RestAPIs/roster/register-absence';
import type { WorkAgreementSetting } from './workAgreementSetting';

const ROLLOUT_DEBOUNCE_MS = 400;

/** duties-in-period per work agreement, as decision info for the choice */
const countDutiesByWaId = (lines: AbsenceRolloutDutyLine[]) => {
  const counts: Record<number, number> = {};
  for (const line of lines) {
    if (line.waId) {
      counts[line.waId] = (counts[line.waId] ?? 0) + 1;
    }
  }
  return counts;
};

type UseAbsenceRolloutArgs = {
  open: boolean;
  /** edit mode does not call the rollout: the consequences follow from the
   *  linked duties, computed by the caller and passed in as `editDutyLines` */
  isEdit: boolean;
  hasValidPeriod: boolean;
  employeeId: number;
  dutyTypeId: number | '';
  startDate: Date | null;
  endDate: Date | null;
  percentage: number;
  workAgreements: RegisterAbsenceWorkAgreement[];
  selectedWaIds: number[];
  workAgreementSettings: Record<number, WorkAgreementSetting>;
  setWorkAgreementSettings: Dispatch<
    SetStateAction<Record<number, WorkAgreementSetting>>
  >;
  editDutyLines: AbsenceRolloutDutyLine[];
};

/** The consequences of the absence for the roster: the planned duties in the
 *  period and the duties the (debounced) rollout proposes to create. */
export const useAbsenceRollout = ({
  open,
  isEdit,
  hasValidPeriod,
  employeeId,
  dutyTypeId,
  startDate,
  endDate,
  percentage,
  workAgreements,
  selectedWaIds,
  workAgreementSettings,
  setWorkAgreementSettings,
  editDutyLines
}: UseAbsenceRolloutArgs) => {
  /**
   * More than 12 hours on a working day is not allowed, and the rollout has no
   * limit of its own: it places a duty in whatever room the day still has
   * (from 09:00, or from midnight when that no longer fits), so 18 hours a day
   * comes back as a duty of 00:00–18:00 and 36 hours comes back as nothing at
   * all. Neither is a duty a planner may create, so the rollout is not asked
   * for at all while the hours are over the limit — the warning under the
   * working days says why, and the consequences panel repeats it.
   */
  const exceedsMaxHoursPerDay = (setting: WorkAgreementSetting) =>
    hoursPerDay(setting.inputWeekHours, setting.inputDays.length, percentage) >
    MAX_HOURS_PER_DAY;

  // the rollout is an explicit opt-in and only makes sense with a start date
  // AND at least one weekday; the backend chokes on a date without days
  // (unparsed-date cast), so both are sent together or not at all.
  // week hours only when filled.
  const toRolloutArgument = (workAgreement: RegisterAbsenceWorkAgreement) => {
    // PNIL never rolls out: always the no-rollout shape, and no week hours —
    // the backend would refuse both anyway
    if (isPnilContract(workAgreement)) {
      return {
        workAgreementId: workAgreement.waId,
        rolloutStartDate: null,
        inputDays: []
      };
    }
    const setting =
      workAgreementSettings[workAgreement.waId] ??
      createDefaultSetting(workAgreement, startDate);
    const hasRollout =
      setting.useFteRollout &&
      !!setting.rolloutStartDate &&
      setting.inputDays.length > 0 &&
      !exceedsMaxHoursPerDay(setting);
    return {
      workAgreementId: workAgreement.waId,
      rolloutStartDate: hasRollout
        ? toBackendDateTime(setting.rolloutStartDate!)
        : null,
      inputDays: hasRollout ? setting.inputDays : [],
      ...(setting.inputWeekHours > 0
        ? { inputWeekHours: setting.inputWeekHours }
        : {})
    };
  };

  /** a selected work agreement wants the rollout but books more than 12 hours
   *  on a working day, so no rollout was asked for it. Editing never rolls out
   *  at all, so it can never be the reason the panel is empty there */
  const overMaxHoursPerDay =
    !isEdit &&
    workAgreements.some((workAgreement) => {
      const setting = workAgreementSettings[workAgreement.waId];

      return (
        !!setting &&
        selectedWaIds.includes(workAgreement.waId) &&
        !isPnilContract(workAgreement) &&
        setting.useFteRollout &&
        setting.inputDays.length > 0 &&
        exceedsMaxHoursPerDay(setting)
      );
    });

  // debounce the rollout inputs so the consequences panel updates ~400ms
  // after the user stops changing dates/percentage/settings.
  // Deliberately NOT keyed on selectedWaIds: checking a work agreement changes
  // nothing the backend is asked, and adding it here cost an extra rollout
  // call on every tick.
  const rolloutInput = useMemo(
    () => ({
      employeeId,
      dutyTypeId: typeof dutyTypeId === 'number' ? dutyTypeId : null,
      startDate: startDate ? toBackendDateTime(startDate) : null,
      endDate: endDate ? toBackendDateTime(endDate, true) : null,
      absencePercentage: percentage,
      workAgreementArguments: workAgreements.map(toRolloutArgument)
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      employeeId,
      dutyTypeId,
      startDate,
      endDate,
      percentage,
      workAgreements,
      workAgreementSettings
    ]
  );
  const [debouncedRolloutInput, setDebouncedRolloutInput] =
    useState(rolloutInput);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedRolloutInput(rolloutInput),
      ROLLOUT_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [rolloutInput]);

  // the hours warning follows the same 400 ms, so it lands together with the
  // answer it explains instead of straight off the checkbox — but on its own
  // timer, because it is a client-side judgement the backend is never asked
  const [debouncedOverMaxHours, setDebouncedOverMaxHours] =
    useState(overMaxHoursPerDay);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedOverMaxHours(overMaxHoursPerDay),
      ROLLOUT_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [overMaxHoursPerDay]);

  /**
   * A change is on its way but the debounce has not fired yet. Without this
   * the panel spends those 400 ms judging the *previous* answer: ticking a
   * working day back under the 12-hour limit made it announce "this changes
   * nothing in the planning" a moment before the duties arrived.
   */
  const isRolloutPending =
    rolloutInput !== debouncedRolloutInput ||
    overMaxHoursPerDay !== debouncedOverMaxHours;

  const rolloutQuery = useQuery(
    ['registerAbsenceRollout', debouncedRolloutInput],
    async () => {
      const { data } = await getAbsenceRollout({
        employeeId: debouncedRolloutInput.employeeId,
        dutyTypeId: debouncedRolloutInput.dutyTypeId!,
        startDate: debouncedRolloutInput.startDate!,
        endDate: debouncedRolloutInput.endDate!,
        absencePercentage: debouncedRolloutInput.absencePercentage,
        workAgreementArguments: debouncedRolloutInput.workAgreementArguments
      });
      return data;
    },
    {
      enabled:
        open &&
        !isEdit &&
        hasValidPeriod &&
        debouncedRolloutInput.dutyTypeId !== null &&
        !!debouncedRolloutInput.startDate &&
        !!debouncedRolloutInput.endDate &&
        debouncedRolloutInput.workAgreementArguments.length > 0,
      keepPreviousData: true,
      // a refused rollout (e.g. missing CLA year record) is reported inside
      // the consequences panel; the app-wide error snackbar would repeat it
      // as a generic failure on every recalculation
      onError: () => {}
    }
  );

  const existingDutyLines = useMemo(
    () => (isEdit ? editDutyLines : rolloutQuery.data?.existingDutyLines ?? []),
    [isEdit, editDutyLines, rolloutQuery.data]
  );
  const newDutyLines = useMemo(() => {
    const lines = isEdit ? [] : rolloutQuery.data?.fteRolloutDutyLines ?? [];
    if (!endDate) {
      return lines;
    }
    // the sickness ends on the expected last day: never create duties past it
    // (the backend rollout rolls out per whole week and spills past the end)
    const firstDayAfter = startOfDay(addDays(endDate, 1));
    return lines.filter((line) =>
      isBefore(parseBackendDateTime(line.startTime), firstDayAfter)
    );
  }, [isEdit, rolloutQuery.data, endDate]);

  const dutyCountByWaId = useMemo(
    () => countDutiesByWaId(existingDutyLines),
    [existingDutyLines]
  );

  const forSelectedWa = (line: AbsenceRolloutDutyLine) =>
    !!line.waId && selectedWaIds.includes(line.waId);
  // new duties are only created for selected work agreements; existing duty
  // lines of unselected work agreements stay visible as "untouched"
  const selectedNewDutyLines = newDutyLines.filter(forSelectedWa);

  /** days the backend refused a duty on (no room left, missing configuration),
   *  so the panel can name them instead of showing a shorter list without a
   *  word about what is missing */
  const rolloutErrorDutyLines = useMemo(
    () =>
      isEdit
        ? []
        : (rolloutQuery.data?.fteRolloutErrorDutyLines ?? []).filter(
            (line) => !!line.waId && selectedWaIds.includes(line.waId)
          ),
    [isEdit, rolloutQuery.data, selectedWaIds]
  );

  // The rollout follows the period on its own: on when the work agreement has
  // no planned duties in it (so the planner sees duties-to-create instead of
  // "nothing happens"), and off again as soon as moving the dates brings a
  // planned duty into range — leaving it on there would create duties next to
  // the ones already planned. An explicit (un)check by the planner always wins.
  useEffect(() => {
    // `open` in the deps: the query data survives a close/reopen in the
    // react-query cache, so without it the opt-in would only happen once
    if (!open || !rolloutQuery.data) {
      return;
    }
    setWorkAgreementSettings((current) => {
      let changed = false;
      const next = { ...current };
      for (const workAgreement of workAgreements) {
        const setting = next[workAgreement.waId];
        if (!setting || setting.useFteRolloutTouched) {
          continue;
        }
        const hasDuties = existingDutyLines.some(
          (line) => line.waId === workAgreement.waId
        );
        // a zero-hours contract is never opted in for the planner: without
        // week hours the rollout would create nothing, and how many hours
        // this absence covers is a decision only the planner can make
        const shouldRollout =
          !isPnilContract(workAgreement) &&
          !isZeroHoursContract(workAgreement) &&
          !hasDuties &&
          setting.inputDays.length > 0;
        if (setting.useFteRollout !== shouldRollout) {
          next[workAgreement.waId] = {
            ...setting,
            useFteRollout: shouldRollout
          };
          changed = true;
        }
      }
      return changed ? next : current;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rolloutQuery.data]);

  return {
    existingDutyLines,
    newDutyLines,
    selectedNewDutyLines,
    dutyCountByWaId,
    rolloutErrorDutyLines,
    // the debounced value: it belongs to the answer on screen, not to the
    // checkbox the planner just clicked
    rolloutOverMaxHoursPerDay: debouncedOverMaxHours,
    // the debounce counts as loading: the panel must not draw a conclusion
    // from the previous answer while the next request is still on its way
    isRolloutLoading:
      isRolloutPending || rolloutQuery.isLoading || rolloutQuery.isFetching,
    // the panel may only judge "nothing happens" on an actual result, never
    // on the emptiness before the first calculation arrived
    hasRolloutResult: !!rolloutQuery.data,
    // backend key of a refused rollout (e.g. missing CLA configuration), so
    // the panel can say why instead of claiming nothing changes
    rolloutErrorKey: (
      rolloutQuery.error as {
        response?: { data?: { error?: { message?: string } } };
      } | null
    )?.response?.data?.error?.message
  };
};
